'use client'

import { Fragment } from 'react'
import { useIntl } from 'react-intl'
import {
  Dialog,
  Cell,
  Text,
  Button,
  Divider,
  Navigation,
  BottomSheet,
  Validation,
} from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  EscrowRefundEstimateConversionElementModel,
  EscrowRefundEstimateModel,
} from '@marketplace-web/complaints/complaints-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'

type Props = {
  refundEstimate?: EscrowRefundEstimateModel | null
  isOpen: boolean
  isOpenedInDialog?: boolean
  onClose: () => void
  isLoading?: boolean
}

const BuyerCurrencyConversionModal = ({
  isOpen,
  onClose,
  refundEstimate,
  isOpenedInDialog = false,
  isLoading,
}: Props) => {
  const { formatNumber, locale } = useIntl()
  const translate = useTranslate('complaint_currency_conversion_dialog')

  const { phones } = useBreakpoint()
  const isMobileView = phones && !isOpenedInDialog

  const conversionElements = refundEstimate?.beforeConversion?.conversionElements

  const renderNavigation = () => (
    <Navigation
      body={translate('title')}
      right={
        <Button
          inline
          iconName={X24}
          styling="flat"
          onClick={onClose}
          aria={{ 'aria-label': translate('a11y.close') }}
        />
      }
    />
  )

  const renderConversionElement = (element: EscrowRefundEstimateConversionElementModel) => {
    const { title, subtitle, money, error } = element

    return (
      <Cell
        title={title}
        body={
          <>
            {subtitle && <Text as="span" type="subtitle" text={subtitle} />}
            {error && <Validation text={error} theme="warning" />}
          </>
        }
        suffix={formatCurrencyAmount(money, locale)}
      />
    )
  }

  const renderConversionElements = () => {
    if (!conversionElements) return null

    return conversionElements.map((element, index) => (
      <Fragment key={index}>{renderConversionElement(element)}</Fragment>
    ))
  }

  const renderExchangeRate = () => {
    if (!refundEstimate?.beforeConversion) return null

    const { baseRate, exchangeRate } = refundEstimate.beforeConversion.exchange

    const base = formatCurrencyAmount(baseRate, locale)
    const rate = formatCurrencyAmount(exchangeRate, locale)

    return (
      <>
        <Divider />
        <Cell>
          <Text
            as="span"
            text={`${translate('fx_rate.title')} ${translate('fx_rate.rate', { rate, base })}`}
          />
        </Cell>
      </>
    )
  }

  const renderPaymentServiceProviderFee = () => {
    if (!refundEstimate?.beforeConversion) return null

    const { rate, provider } = refundEstimate.beforeConversion.markup

    const feePercentage = formatNumber(rate, {
      style: 'percent',
      maximumFractionDigits: 2,
    })

    return (
      <Cell
        body={
          <Text
            as="h3"
            html
            type="subtitle"
            text={translate('fx_markup_rate', { fee: feePercentage, payment_provider: provider })}
          />
        }
      />
    )
  }

  const renderActions = () => (
    <Cell>
      <Button text={translate('actions.ok')} styling="filled" onClick={onClose} />
    </Cell>
  )

  const renderContent = () =>
    isLoading ? (
      <ContentLoader styling={ContentLoader.Styling.Wide} />
    ) : (
      <>
        <SeparatedList separator={<Divider />}>
          {renderNavigation()}
          {renderConversionElements()}
        </SeparatedList>
        {renderExchangeRate()}
        {renderPaymentServiceProviderFee()}
        {!isMobileView && renderActions()}
      </>
    )

  if (isMobileView) {
    return (
      <BottomSheet isVisible={isOpen} onClose={onClose}>
        {renderContent()}
      </BottomSheet>
    )
  }

  return (
    <Dialog show={isOpen} defaultCallback={onClose} closeOnOverlay>
      {renderContent()}
    </Dialog>
  )
}

export default BuyerCurrencyConversionModal
